import { useRef, useState } from 'react';
import { BRAND } from '../lib/brand.js';
import { useFocusTrap } from '../lib/use-focus-trap.js';
import ExternalLink from './ExternalLink.jsx';

// Line icons on the sidebar grid (24 x 24, stroke 1.75, round caps, currentColor).
const ICON_HELP = 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9.5 9.5a2.5 2.5 0 1 1 3.6 2.25c-.7.35-1.1.9-1.1 1.75M12 17h.01';
const ICON_SIGN_OUT = 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9';

function LineIcon({ d }) {
  return (
    <svg aria-hidden="true" className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

function formatLastSignIn(iso) {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

function Detail({ label, children }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</dt>
      <dd className="text-sm text-ipc-text">{children}</dd>
    </div>
  );
}

const ITEM = 'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-ipc-primary hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ipc-light';

/**
 * The TopBar user menu: who is signed in (name, email, company, roles in this
 * company, how they signed in, when), then Get help and Sign out. The profile
 * comes from lib/user-profile.js, built only from what a native and a company
 * SSO token both carry, so it reads the same either way. No tokens or ids.
 *
 * Keyboard: Tab and Shift+Tab stay in the panel, ArrowDown/ArrowUp/Home/End
 * move between the actions, Escape closes and returns focus to the button.
 */
export default function UserMenu({ profile, onSignOut }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  useFocusTrap(panelRef, { active: open, onClose: () => setOpen(false), returnFocusRef: triggerRef });
  const label = profile?.name || profile?.email || '';
  const initial = (label || '?').charAt(0).toUpperCase();
  const lastSignIn = profile?.lastSignInAt ? formatLastSignIn(profile.lastSignInAt) : null;
  const roles = profile?.roles ?? [];

  function onKeyDown(event) {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const items = [...panelRef.current.querySelectorAll('[data-menu-item]')];
    if (items.length === 0) return;
    event.preventDefault();
    const at = items.indexOf(document.activeElement);
    let next = 0;
    if (event.key === 'End') next = items.length - 1;
    else if (event.key === 'ArrowDown') next = at < 0 ? 0 : (at + 1) % items.length;
    else if (event.key === 'ArrowUp') next = at <= 0 ? items.length - 1 : at - 1;
    items[next].focus();
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="tap flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      >
        <span aria-hidden="true" data-avatar="" className="flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-xs font-semibold">{initial}</span>
        <span className="hidden max-w-[12rem] truncate lg:inline">{label}</span>
        <span className="sr-only">Account menu</span>
      </button>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Account"
          onKeyDown={onKeyDown}
          className="on-light absolute right-0 z-40 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-ipc-border bg-white p-2 text-ipc-text shadow-xl"
        >
          <div className="flex items-start gap-3 px-3 py-2">
            <span aria-hidden="true" data-avatar="" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ipc-primary text-base font-semibold text-white">{initial}</span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ipc-primary">{profile?.name || profile?.email}</p>
              {profile?.name && profile?.email && <p className="truncate text-xs text-slate-600">{profile.email}</p>}
            </div>
          </div>
          <dl className="space-y-2 border-t border-ipc-border px-3 py-3">
            {profile?.company && <Detail label="Company">{profile.company}</Detail>}
            {roles.length > 0 && (
              <Detail label="Roles">
                <ul aria-label="Roles" className="m-0 flex list-none flex-wrap gap-1 p-0">
                  {roles.map((role) => <li key={role}><span className="badge-slate">{role}</span></li>)}
                </ul>
              </Detail>
            )}
            {profile?.signIn && <Detail label="Signed in with">{profile.signIn}</Detail>}
            {lastSignIn && <Detail label="Last sign-in">{lastSignIn}</Detail>}
          </dl>
          <div className="space-y-1 border-t border-ipc-border pt-2">
            <ExternalLink href={BRAND.supportUrl} data-menu-item className={ITEM}>
              <LineIcon d={ICON_HELP} />
              <span>Get help</span>
            </ExternalLink>
            <button type="button" data-menu-item className={ITEM} onClick={onSignOut}>
              <LineIcon d={ICON_SIGN_OUT} />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
